import { createMuiTheme } from '@material-ui/core/styles';

export const tokens = {
    primary: '#4de68c',
    primaryHover: '#32d979',
    primarySoft: '#dff9eb',
    onPrimary: '#14161f',

    ink: '#14161f',
    inkDeep: '#070b16',
    muted: '#566071',
    subtle: '#7f8999',

    surface: '#ffffff',
    canvas: '#f2f4f8',
    border: '#e7eaf0',
    borderSoft: '#eef1f5',
    hover: '#f4f6f8',

    success: '#096844',
    warning: '#9a3d13',
    danger: '#b3145a',
    info: '#153e9d',
    violet: '#4e299c',
    blue: '#2563eb',

    metricSurfaces: {
        green: '#4de68c',
        blue: '#e8f0ff',
        purple: '#f0eaff',
        orange: '#fff0e7',
    },
    metricForegrounds: {
        green: '#14161f',
        blue: '#153e9d',
        purple: '#4e299c',
        orange: '#9a3d13',
    },

    radiusCard: 20,
    radiusControl: 14,
    radiusPill: 999,
    panelShadow: '0 16px 45px rgba(25, 33, 50, 0.07), 0 2px 8px rgba(25, 33, 50, 0.035)',
};

// The sidebar is a fixed-width rail. React Admin sizes the drawer from
// `theme.sidebar.width`, but our custom <BrandedMenu> renders inside a
// position:fixed container whose containing block is the VIEWPORT, not the
// drawer — so it cannot inherit that width as a percentage and must be pinned
// to the same number minus the drawer paper's horizontal padding. Without the
// pin the rail is sized by whichever menu group happens to be expanded, so it
// visibly grows and shrinks as you change tab.
export const SIDEBAR_WIDTH = 268;
export const SIDEBAR_PADDING_X = 8;
export const SIDEBAR_INNER_WIDTH = SIDEBAR_WIDTH - SIDEBAR_PADDING_X * 2;

const display = ['Bricolage Grotesque', 'system-ui', 'sans-serif'].join(',');
const text = ['Plus Jakarta Sans', 'system-ui', 'sans-serif'].join(',');

const shadows = [...createMuiTheme().shadows];
for (let i = 1; i <= 8; i++) shadows[i] = tokens.panelShadow;
shadows[9] = '0 8px 24px rgba(20, 22, 31, 0.12)';
shadows[16] = '0 18px 48px rgba(20, 22, 31, 0.16)';

const focusVisible = {
    outline: `3px solid ${tokens.ink}`,
    outlineOffset: 3,
};

const theme = {
    palette: {
        type: 'light',
        primary: { main: tokens.primary, dark: tokens.primaryHover, contrastText: tokens.onPrimary },
        secondary: { main: tokens.ink, contrastText: '#fff' },
        error: { main: tokens.danger },
        warning: { main: tokens.warning },
        success: { main: tokens.success },
        info: { main: tokens.info },
        background: { default: tokens.canvas, paper: tokens.surface },
        text: { primary: tokens.ink, secondary: tokens.muted, disabled: tokens.subtle },
        divider: tokens.borderSoft,
    },

    shape: { borderRadius: tokens.radiusControl },
    sidebar: { width: SIDEBAR_WIDTH, closedWidth: 55 },
    shadows,

    typography: {
        fontFamily: text,
        h1: { fontFamily: display, fontSize: 32, fontWeight: 800, letterSpacing: '-0.025em', color: tokens.ink },
        h2: { fontFamily: display, fontSize: 26, fontWeight: 800, letterSpacing: '-0.025em', color: tokens.ink },
        h3: { fontFamily: display, fontSize: 21, fontWeight: 700, letterSpacing: '-0.02em', color: tokens.ink },
        h4: { fontFamily: display, fontSize: 20, fontWeight: 700, letterSpacing: '-0.02em', color: tokens.ink },
        h5: { fontFamily: display, fontSize: 18, fontWeight: 700, letterSpacing: '-0.02em', color: tokens.ink },
        h6: { fontFamily: display, fontSize: 16, fontWeight: 700, color: tokens.ink },
        body1: { fontSize: 14 },
        body2: { fontSize: 14 },
        caption: { fontSize: 12, color: tokens.muted },
        button: { fontFamily: text, fontSize: 13, fontWeight: 700, textTransform: 'none', letterSpacing: '0.005em' },
    },

    props: {
        MuiPaper: { elevation: 0 },
        MuiCard: { elevation: 0 },
        MuiAppBar: { elevation: 0 },
        MuiTextField: { variant: 'outlined', size: 'small' },
        MuiButton: { disableElevation: true },
    },

    overrides: {
        MuiCssBaseline: {
            '@global': {
                body: { backgroundColor: tokens.canvas, color: tokens.ink },
                '*:focus-visible': focusVisible,
            },
        },
        MuiPaper: {
            rounded: { borderRadius: tokens.radiusCard },
            elevation0: {
                border: '1px solid rgba(20, 22, 31, 0.045)',
                boxShadow: tokens.panelShadow,
            },
        },
        MuiCardHeader: {
            root: { padding: '20px 22px 0' },
            title: { fontFamily: display, fontWeight: 800, letterSpacing: '-0.02em' },
        },
        MuiButton: {
            root: {
                borderRadius: tokens.radiusControl,
                padding: '9px 18px',
                minHeight: 40,
                '&:focus-visible': focusVisible,
            },
            contained: { boxShadow: 'none', '&:hover': { boxShadow: '0 9px 22px rgba(77, 230, 140, 0.24)' } },
            containedPrimary: { color: tokens.onPrimary, '&:hover': { backgroundColor: tokens.primaryHover } },
            outlinedPrimary: {
                color: tokens.ink,
                borderColor: tokens.border,
                '&:hover': { borderColor: tokens.primary, backgroundColor: tokens.primarySoft },
            },
            textSecondary: { color: tokens.danger },
        },
        MuiIconButton: {
            root: { borderRadius: tokens.radiusControl, '&:focus-visible': focusVisible },
        },
        MuiOutlinedInput: {
            root: {
                borderRadius: tokens.radiusControl,
                backgroundColor: tokens.surface,
                '& fieldset': { borderColor: tokens.border },
                '&:hover fieldset': { borderColor: tokens.subtle },
                '&.Mui-focused fieldset': { borderWidth: 1, borderColor: tokens.primary },
            },
        },
        MuiTableCell: {
            root: { borderBottom: `1px solid ${tokens.borderSoft}`, padding: '12px 16px', fontSize: 14 },
            head: {
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                color: tokens.muted,
                backgroundColor: tokens.surface,
            },
        },
        MuiTableRow: { root: { '&:hover': { backgroundColor: tokens.hover } } },
        MuiChip: { root: { borderRadius: tokens.radiusPill, fontWeight: 700, fontSize: 12 } },
        MuiMenu: { paper: { borderRadius: tokens.radiusControl, boxShadow: shadows[9] } },
        MuiDialog: { paper: { borderRadius: tokens.radiusCard, boxShadow: shadows[16] } },
        MuiTooltip: { tooltip: { backgroundColor: tokens.inkDeep, fontSize: 12, borderRadius: 10 } },

        RaAppBar: {
            toolbar: {
                minHeight: 70,
                backgroundColor: tokens.surface,
                color: tokens.ink,
                borderBottom: `1px solid ${tokens.border}`,
                boxShadow: 'none',
            },
        },
        RaSidebar: {
            root: {
                minHeight: 'calc(100vh - 73px)',
                height: 'calc(100vh - 73px)',
                // No surface fill or divider: the menu floats directly on the
                // app canvas, so there is no white panel sitting behind (and
                // wider than) the menu items.
                backgroundColor: 'transparent',
                borderRight: 'none',
                width: SIDEBAR_WIDTH,
                minWidth: SIDEBAR_WIDTH,
                flex: `0 0 ${SIDEBAR_WIDTH}px`,
            },
            fixed: {
                backgroundColor: 'transparent',
                height: '100%',
                width: SIDEBAR_INNER_WIDTH,
                minWidth: SIDEBAR_INNER_WIDTH,
                maxWidth: SIDEBAR_INNER_WIDTH,
                boxSizing: 'border-box',
            },
            drawerPaper: {
                minHeight: '100%',
                height: '100%',
                boxSizing: 'border-box',
                backgroundColor: 'transparent',
                color: tokens.ink,
                padding: `14px ${SIDEBAR_PADDING_X}px 18px`,
                borderRight: 'none',
                // The global MuiPaper elevation0 override gives every Paper a
                // hairline border and drop shadow. Its rule is injected after
                // this one, so an equal-specificity `boxShadow: none` loses.
                // `&&` doubles the generated class in the selector, which wins
                // on specificity without reaching for !important.
                '&&': {
                    boxShadow: 'none',
                    border: 'none',
                },
                width: SIDEBAR_WIDTH,
                minWidth: SIDEBAR_WIDTH,
            },
        },
        RaMenuItemLink: {
            root: {
                color: tokens.muted,
                borderRadius: tokens.radiusControl,
                margin: '3px 8px',
                padding: '10px 14px',
                minHeight: 43,
                fontSize: 13,
                fontWeight: 600,
                transition: 'transform .16s ease, background-color .16s ease, color .16s ease',
                '&:hover': { backgroundColor: tokens.hover, color: tokens.ink, transform: 'translateX(2px)' },
                '&:focus-visible': focusVisible,
            },
            active: {
                color: tokens.onPrimary,
                backgroundColor: tokens.primary,
                fontWeight: 800,
                boxShadow: '0 9px 22px rgba(77, 230, 140, 0.28)',
                '&:hover': { backgroundColor: tokens.primaryHover, color: tokens.onPrimary },
            },
            icon: { color: 'inherit', minWidth: 34 },
        },
        RaLayout: {
            root: { minWidth: 0, width: '100%', maxWidth: '100%', boxSizing: 'border-box' },
            appFrame: {
                backgroundColor: tokens.canvas,
                minWidth: 0,
                width: '100%',
                /* 70px toolbar + its 1px divider + the Paper shell's 1px top
                   and bottom borders render as a 73px fixed app bar. */
                marginTop: 73,
                /* RA 3 reapplies 48/56px inside breakpoint rules. Match that
                   specificity so the tall toolbar cannot overlap content. */
                '@media (min-width: 0px)': { marginTop: 73 },
            },
            contentWithSidebar: { minWidth: 0, width: '100%' },
            content: {
                backgroundColor: tokens.canvas,
                padding: '28px 30px 42px',
                minWidth: 0,
                maxWidth: '100%',
                boxSizing: 'border-box',
            },
        },
        RaDatagrid: {
            headerCell: { backgroundColor: tokens.surface, borderBottom: `1px solid ${tokens.border}` },
            rowCell: { color: tokens.ink },
        },
        RaBulkActionsToolbar: { toolbar: { backgroundColor: tokens.primarySoft, borderRadius: tokens.radiusControl } },
    },
};

export default theme;
